<template>
  <div class="podbor-page">
    <header-main title="Подбор" :hide-order-button="true" />

    <div class="podbor-layout">
      <section class="podbor-card">
        <div class="podbor-card__header">
          <div>
            <h2 class="podbor-card__title">Подбор по дереву</h2>
            <p class="podbor-card__subtitle">
              Выберите подходящую конфигурацию и укажите стоимость установки для каждого уровня.
            </p>
          </div>

          <div class="podbor-card__controls">
            <div class="control-group">
              <label for="podbor-search">Поиск</label>
              <input
                id="podbor-search"
                v-model.trim="searchTerm"
                type="text"
                class="input-field control-input"
                placeholder="Начните вводить название"
              />
            </div>

            <label class="control-checkbox">
              <input type="checkbox" v-model="showOnlyPriced" />
              <span>Только с ценой</span>
            </label>

            <button class="btn btn_success control-reset" @click="resetTree">
              Сбросить
            </button>

            <button 
              class="btn btn_primary control-save" 
              @click="savePrices"
              :disabled="isSaving || filledNodesCount === 0"
            >
              <span v-if="isSaving">Сохранение...</span>
              <span v-else>Сохранить</span>
            </button>
          </div>
        </div>

        <div v-if="isLoading" class="data-placeholder">
          <span class="loader-dot"></span>
          <span class="loader-dot"></span>
          <span class="loader-dot"></span>
          <p>Загружаем дерево подбора...</p>
        </div>

        <div v-else-if="errorMessage" class="data-placeholder data-placeholder_error">
          <p>{{ errorMessage }}</p>
          <button class="btn btn_success retry-btn" @click="fetchTreeData">Повторить запрос</button>
        </div>

        <div v-if="saveMessage" class="save-message" :class="{ 'save-message_success': !saveMessage.includes('ошибка'), 'save-message_error': saveMessage.includes('ошибка') }">
          <p>{{ saveMessage }}</p>
        </div>

        <template v-else>
          <div class="podbor-summary">
            <div class="summary-item">
              <span class="summary-label">Выбрано элементов</span>
              <span class="summary-value">{{ filledNodesCount }}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Последнее обновление</span>
              <span class="summary-value">{{ lastUpdatedLabel }}</span>
            </div>
          </div>

          <div class="podbor-tree">
            <div v-if="filteredTree.length" class="tree-root">
              <tree-node
                v-for="node in filteredTree"
                :key="node.id"
                :node="node"
                :level="0"
                :search-term="searchTerm"
                :show-only-priced="showOnlyPriced"
                @price-change="handlePriceChange"
              />
            </div>
            <div v-else class="empty-state">
              <p>По запросу ничего не найдено. Попробуйте изменить фильтры.</p>
            </div>
          </div>
        </template>
      </section>

      <aside class="podbor-sidebar">
        <div class="sidebar-card">
          <h3>Подсказки</h3>
          <ul class="tips-list">
            <li>Стоимость можно указать для любого уровня дерева.</li>
            <li>Иконка рядом с названием показывает статус заполнения ветки.</li>
            <li>В боковой панели отображаются все элементы с указанной ценой.</li>
          </ul>
        </div>

        <div class="sidebar-card">
          <h3>Стоимость по веткам</h3>
          <div v-if="isLoading" class="empty-state small">
            <p>Загрузка данных...</p>
          </div>
          <div v-else-if="errorMessage" class="empty-state small">
            <p>Информация будет доступна после успешной загрузки данных.</p>
          </div>
          <div v-else-if="pricedNodes.length" class="priced-list">
            <div v-for="item in pricedNodes" :key="item.id" class="priced-item">
              <span class="priced-name">{{ item.breadcrumb }}</span>
              <span class="priced-value">{{ item.price | currency }}</span>
            </div>
          </div>
          <div v-else class="empty-state small">
            <p>Заполните стоимость, чтобы увидеть результат.</p>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { ORDERS_API_BASE } from '@/config/api';
import axios from 'axios';
import HeaderMain from '@/components/HeaderMain.vue';

const hasPrice = (node) => node.price !== null && node.price !== undefined && node.price !== '';

const branchHasPrice = (node) => {
  if (hasPrice(node)) {
    return true;
  }
  const children = Array.isArray(node.children) ? node.children : [];
  return children.some((child) => branchHasPrice(child));
};

const matchesCriteria = (node, searchTerm, showOnlyPriced) => {
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const title = node.name.toLowerCase();
  const keywords = (node.keywords || []).map((item) => item.toLowerCase());
  const searchMatch =
    !normalizedSearch ||
    title.includes(normalizedSearch) ||
    keywords.some((keyword) => keyword.includes(normalizedSearch));

  const priceMatch = !showOnlyPriced || hasPrice(node) || branchHasPrice(node);
  const children = Array.isArray(node.children) ? node.children : [];
  const childMatch = children.some((child) => matchesCriteria(child, searchTerm, showOnlyPriced));

  return (searchMatch && priceMatch) || childMatch;
};

const collectPriced = (nodes, trail = [], acc = []) => {
  nodes.forEach((node) => {
    const currentTrail = [...trail, node.name];
    if (hasPrice(node)) {
      acc.push({
        id: node.id,
        type: node.type || 'element',
        price: Number(node.price) || 0,
        breadcrumb: currentTrail.join(' > '),
      });
    }
    if (Array.isArray(node.children) && node.children.length) {
      collectPriced(node.children, currentTrail, acc);
    }
  });
  return acc;
};

const collectChanged = (currentNodes, initialNodes, trail = [], acc = []) => {
  currentNodes.forEach((currentNode) => {
    const currentTrail = [...trail, currentNode.name];
    
    // Находим соответствующий узел в начальном снимке
    const initialNode = initialNodes.find((node) => node.id === currentNode.id);
    const initialPrice = initialNode ? initialNode.price : null;
    const currentPrice = currentNode.price;
    
    // Собираем элементы, которые:
    // 1. Имеют цену (price !== null) ИЛИ
    // 2. Имели цену в начальном снимке, но теперь не имеют (была удалена)
    const hasPriceNow = hasPrice(currentNode);
    const hadPriceBefore = initialPrice !== null && initialPrice !== undefined;
    const priceChanged = currentPrice !== initialPrice;
    
    if (hasPriceNow || (hadPriceBefore && priceChanged)) {
      acc.push({
        id: currentNode.id,
        type: currentNode.type || 'element',
        price: currentPrice === null || currentPrice === undefined ? null : Number(currentPrice),
        breadcrumb: currentTrail.join(' > '),
      });
    }
    
    if (Array.isArray(currentNode.children) && currentNode.children.length) {
      const initialChildren = initialNode && Array.isArray(initialNode.children) ? initialNode.children : [];
      collectChanged(currentNode.children, initialChildren, currentTrail, acc);
    }
  });
  return acc;
};

const resetPrices = (nodes) => {
  nodes.forEach((node) => {
    node.price = null;
    if (Array.isArray(node.children) && node.children.length) {
      resetPrices(node.children);
    }
  });
};

const TreeNode = {
  name: 'TreeNode',
  props: {
    node: {
      type: Object,
      required: true,
    },
    level: {
      type: Number,
      default: 0,
    },
    searchTerm: {
      type: String,
      default: '',
    },
    showOnlyPriced: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: this.node.expanded === true,
    };
  },
  computed: {
    hasChildren() {
      return Array.isArray(this.node.children) && this.node.children.length > 0;
    },
    filteredChildren() {
      if (!this.hasChildren) {
        return [];
      }
      return this.node.children.filter((child) =>
        matchesCriteria(child, this.searchTerm, this.showOnlyPriced),
      );
    },
    shouldRender() {
      return matchesCriteria(this.node, this.searchTerm, this.showOnlyPriced);
    },
    statusClass() {
      if (hasPrice(this.node)) {
        return 'status-filled';
      }
      if (this.hasChildren && this.node.children.some((child) => branchHasPrice(child))) {
        return 'status-partial';
      }
      return 'status-empty';
    },
    indentStyle() {
      return {
        marginLeft: `${this.level * 24}px`,
      };
    },
    priceProxy: {
      get() {
        return this.node.price === null || this.node.price === undefined
          ? null
          : Number(this.node.price);
      },
      set(value) {
        if (value === '' || value === null || value === undefined || Number.isNaN(Number(value))) {
          this.node.price = null;
        } else {
          this.node.price = Number(value);
        }
        this.notifyChange();
      },
    },
    hasOwnPrice() {
      return hasPrice(this.node);
    },
  },
  watch: {
    searchTerm() {
      if (this.searchTerm && this.shouldRender) {
        this.isExpanded = true;
        this.node.expanded = true;
      }
    },
    showOnlyPriced(newValue) {
      if (newValue && !branchHasPrice(this.node) && !hasPrice(this.node)) {
        this.isExpanded = false;
        this.node.expanded = false;
      }
    },
  },
  methods: {
    toggle() {
      if (this.hasChildren) {
        this.isExpanded = !this.isExpanded;
        this.node.expanded = this.isExpanded;
      }
    },
    notifyChange() {
      this.$emit('price-change', {
        node: this.node,
        value: this.node.price,
      });
    },
    clearPrice() {
      this.node.price = null;
      this.notifyChange();
    },
  },
  render(h) {
    if (!this.shouldRender) {
      return null;
    }

    var self = this;
    var scopeId = (this.$parent && this.$parent.$options && this.$parent.$options._scopeId) || null;

    function withScope(data) {
      data = data || {};
      if (!scopeId) {
        return data;
      }
      if (!data.attrs) {
        data.attrs = {};
      }
      data.attrs[scopeId] = '';
      return data;
    }
    var childrenNodes = null;

    if (this.hasChildren && this.isExpanded) {
      childrenNodes = this.filteredChildren.map(function(child) {
        return h('TreeNode', {
          props: {
            node: child,
            level: self.level + 1,
            searchTerm: self.searchTerm,
            showOnlyPriced: self.showOnlyPriced,
          },
          on: {
            'price-change': function(payload) {
              self.$emit('price-change', payload);
            },
          },
          key: child.id,
        });
      });
    }

    var nodeMetaChildren = [
      h('span', withScope({ class: 'node-title' }), this.node.name),
    ];

    if (Array.isArray(this.node.tags) && this.node.tags.length) {
      var tagNodes = this.node.tags.map(function(tag) {
        return h('span', withScope({ class: 'tag-item', key: self.node.id + '-' + tag }), tag);
      });
      nodeMetaChildren.push(h('div', withScope({ class: 'tag-list' }), tagNodes));
    }

    var infoBlock = h(
      'div',
      withScope({ class: 'node-info' }),
      [
        this.hasChildren
          ? h(
              'button',
              withScope({
                class: ['toggle-btn', { 'toggle-btn_open': this.isExpanded }],
                attrs: { type: 'button' },
                on: { click: this.toggle },
              }),
              [this.isExpanded ? '−' : '+'],
            )
          : h('span', withScope({ class: 'toggle-placeholder' })),
        h('span', withScope({ class: ['status-indicator', this.statusClass] })),
        h('div', withScope({ class: 'node-meta' }), nodeMetaChildren),
      ],
    );

    var actionChildren = [
      h('input', withScope({
        class: ['input-field', 'price-input'],
        domProps: {
          value: this.priceProxy === null || this.priceProxy === undefined ? '' : this.priceProxy,
        },
        attrs: {
          type: 'number',
          min: '0',
          step: '10',
          placeholder: 'Стоимость, руб.',
        },
        on: {
          input: function(event) {
            var value = event.target.value;
            if (value === '' || value === null || value === undefined) {
              self.priceProxy = null;
            } else {
              self.priceProxy = Number(value);
            }
          },
        },
      })),
    ];

    if (this.hasOwnPrice) {
      actionChildren.push(
        h(
          'button',
          withScope({
            class: 'clear-btn',
            attrs: { type: 'button', title: 'Очистить стоимость' },
            on: { click: this.clearPrice },
          }),
          ['×'],
        ),
      );
    }

    var actionBlock = h('div', withScope({ class: 'node-actions' }), actionChildren);

    return h(
      'div',
      withScope({
        class: ['tree-node', { 'tree-node_leaf': !this.hasChildren, 'tree-node_root': this.level === 0 }],
      }),
      [
        h(
          'div',
          withScope({
            class: 'node-row',
            style: this.indentStyle,
          }),
          [
            infoBlock,
            actionBlock,
          ],
        ),
        childrenNodes
          ? h(
              'transition-group',
              withScope({
                class: 'node-children',
                props: { name: 'tree-fade', tag: 'div' },
              }),
              childrenNodes,
            )
          : null,
      ],
    );
  },
};

export default {
  name: 'PodborTree',
  components: {
    HeaderMain,
    TreeNode,
  },
  data() {
    return {
      searchTerm: '',
      showOnlyPriced: false,
      lastUpdated: null,
      treeData: [],
      initialTreeSnapshot: [],
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      saveMessage: '',
      endpoint: 'https://1akb.by/local/api/bitrix_podbor_tree.php',
      saveEndpoint: ORDERS_API_BASE + '/save_podbor_prices.php',
    };
  },
  created() {
    this.fetchTreeData();
  },
  computed: {
    filteredTree() {
      return this.treeData.filter((node) => matchesCriteria(node, this.searchTerm, this.showOnlyPriced));
    },
    pricedNodes() {
      return collectPriced(this.treeData);
    },
    filledNodesCount() {
      return this.pricedNodes.length;
    },
    lastUpdatedLabel() {
      if (!this.lastUpdated) {
        return '—';
      }
      return new Date(this.lastUpdated).toLocaleString('ru-RU', {
        hour12: false,
      });
    },
  },
  methods: {
    async fetchTreeData() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        const response = await axios.get(this.endpoint, {
          params: {
            depth: 4,
          },
        });
        let payload = [];
        if (Array.isArray(response.data)) {
          payload = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          payload = response.data.data;
        }
        const normalized = this.normalizeTree(payload);
        this.treeData = normalized;
        this.initialTreeSnapshot = this.cloneTree(normalized);
      } catch (error) {
        console.error('Ошибка загрузки дерева подбора:', error);
        this.errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          'Не удалось загрузить данные подбора. Попробуйте обновить страницу позже.';
      } finally {
        this.isLoading = false;
      }
    },
    normalizeTree(nodes) {
      if (!Array.isArray(nodes)) {
        return [];
      }
      return nodes.map((node) => {
        const children = Array.isArray(node.children) ? this.normalizeTree(node.children) : [];
        const priceValue =
          node.price !== null && node.price !== undefined && !Number.isNaN(Number(node.price))
            ? Number(node.price)
            : null;
        return {
          id: node.id || node.ID,
          type: node.type || 'element',
          name: node.name || node.NAME || 'Без названия',
          description: node.description || node.DESCRIPTION || '',
          price: priceValue,
          keywords: node.keywords || node.KEYWORDS || [],
          tags: node.tags || node.TAGS || [],
          expanded: node.expanded === true,
          children,
        };
      });
    },
    cloneTree(nodes) {
      return nodes.map((node) => ({
        ...node,
        type: node.type || 'element',
        price: node.price,
        expanded: node.expanded === true,
        children: Array.isArray(node.children) ? this.cloneTree(node.children) : [],
      }));
    },
    restoreInitialTree() {
      this.treeData = this.cloneTree(this.initialTreeSnapshot);
    },
    handlePriceChange() {
      this.lastUpdated = Date.now();
    },
    resetTree() {
      this.restoreInitialTree();
      this.searchTerm = '';
      this.showOnlyPriced = false;
      this.lastUpdated = null;
      this.saveMessage = '';
    },
    async savePrices() {
      this.isSaving = true;
      this.saveMessage = '';
      this.errorMessage = '';

      try {
        // Собираем все измененные элементы, включая те, у которых цена была удалена
        const changedNodes = collectChanged(this.treeData, this.initialTreeSnapshot);
        
        if (changedNodes.length === 0) {
          this.saveMessage = 'Нет изменений для сохранения';
          setTimeout(() => {
            this.saveMessage = '';
          }, 3000);
          this.isSaving = false;
          return;
        }

        // Формируем данные для отправки
        const pricesData = changedNodes.map((node) => ({
          id: node.id,
          type: node.type || 'element',
          price: node.price,
        }));

        const response = await axios.post(
          this.saveEndpoint,
          {
            prices: pricesData,
            iblockId: 5, // ID инфоблока по умолчанию
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.data && response.data.success) {
          this.saveMessage = response.data.message || `Успешно сохранено элементов: ${response.data.updated || pricesData.length}`;
          
          // Обновляем дерево, чтобы получить актуальные данные с сервера
          await this.fetchTreeData();
          
          // Очищаем сообщение через 5 секунд
          setTimeout(() => {
            this.saveMessage = '';
          }, 5000);
        } else {
          throw new Error((response.data && response.data.message) || 'Ошибка при сохранении цен');
        }
      } catch (error) {
        console.error('Ошибка сохранения цен:', error);
        this.saveMessage = 
          (error.response && error.response.data && error.response.data.message) ||
          'Не удалось сохранить цены. Попробуйте еще раз.';
          
        // Очищаем сообщение об ошибке через 5 секунд
        setTimeout(() => {
          this.saveMessage = '';
        }, 5000);
      } finally {
        this.isSaving = false;
      }
    },
  },
  filters: {
    currency(value) {
      const numeric = Number(value);
      if (!numeric) {
        return '0 руб.';
      }
      return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'BYN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(numeric);
    },
  },
};
</script>

<style>
.podbor-page {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.podbor-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 16px;
}

.podbor-card {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(30, 34, 72, 0.08);
}

.podbor-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 24px;
}

.data-placeholder {
  border: 1px dashed #c7cdd4;
  border-radius: 8px;
  padding: 48px 16px;
  text-align: center;
  background: #f9fafb;
  color: #636f7f;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.data-placeholder_error {
  border-color: rgba(255, 83, 83, 0.4);
  background: #fff5f5;
  color: #b73232;
}

.loader-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #005eeb;
  display: inline-block;
  animation: loader-bounce 1.2s infinite ease-in-out;
  margin: 0 4px;
}

.loader-dot:nth-child(2) {
  animation-delay: -0.2s;
}

.loader-dot:nth-child(1) {
  animation-delay: -0.4s;
}

@keyframes loader-bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.retry-btn {
  margin-top: 8px;
  padding: 6px 16px;
}

.podbor-card__title {
  font-size: 20px;
  font-weight: 600;
  color: #1e2248;
  margin-bottom: 6px;
}

.podbor-card__subtitle {
  max-width: 520px;
  color: #636f7f;
  font-size: 13px;
}

.podbor-card__controls {
  display: flex;
  align-items: center;
  gap: 16px;
}

.control-group {
  display: flex;
  flex-direction: column;
  font-size: 12px;
  color: #636f7f;
  min-width: 220px;
}

.control-input {
  width: 100%;
  height: 34px;
}

.control-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1e2248;
  cursor: pointer;
}

.control-checkbox input {
  width: 18px;
  height: 18px;
}

.control-reset {
  height: 34px;
  padding: 0 20px;
}

.control-save {
  height: 34px;
  padding: 0 20px;
}

.control-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.save-message {
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 6px;
  font-size: 13px;
}

.save-message_success {
  background: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.save-message_error {
  background: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.podbor-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  border: 1px solid #eef1f3;
  border-radius: 6px;
  padding: 12px 16px;
  background: #f9fafb;
  margin-bottom: 24px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-label {
  font-size: 11px;
  color: #8a96a6;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.summary-value {
  font-size: 18px;
  font-weight: 600;
  color: #1e2248;
}

.podbor-tree {
  border: 1px solid #eef1f3;
  border-radius: 8px;
  background: #fdfdfd;
  max-height: calc(100vh - 220px);
  overflow-y: auto;
}

.tree-root {
  padding: 8px 0;
}

.tree-node {
  display: flex;
  flex-direction: column;
  padding: 6px 16px;
  position: relative;
}

.tree-node::before {
  content: '';
  position: absolute;
  left: 28px;
  top: 0;
  bottom: -6px;
  border-left: 1px dashed #d5deeb;
}

.tree-node_root::before {
  display: none;
}

.tree-node > .node-row::before {
  content: '';
  position: absolute;
  left: 28px;
  top: 22px;
  width: 18px;
  border-bottom: 1px dashed #d5deeb;
}

.tree-node_root > .node-row::before {
  display: none;
}

.node-row {
  display: flex;
  align-items: stretch;
  gap: 14px;
  padding: 10px 14px;
  transition: background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
  min-width: 0;
  background: linear-gradient(115deg, #ffffff 0%, #f7faff 100%);
  border: 1px solid #dfe6f1;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(30, 34, 72, 0.06);
  position: relative;
}

.node-row:hover {
  background: linear-gradient(115deg, #f6f9ff 0%, #ecf2ff 100%);
  border-color: #c6d9ff;
  box-shadow: 0 6px 16px rgba(0, 94, 235, 0.16);
}

.node-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 auto;
  min-width: 0;
}

.node-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 auto;
  padding-left: 14px;
  border-left: 1px dashed #e1e7f3;
}

.toggle-btn {
  width: 26px;
  height: 26px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #ffffff;
  color: #1e2248;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(30, 34, 72, 0.12);
}

.toggle-btn_open {
  background: #005eeb;
  color: #ffffff;
  border-color: #005eeb;
}

.toggle-placeholder {
  width: 26px;
  height: 26px;
  flex-shrink: 0;
}

.status-indicator {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
  margin-right: 2px;
}

.status-filled {
  background: #22c993;
  box-shadow: 0 0 0 3px rgba(34, 201, 147, 0.2);
}

.status-partial {
  background: #fea530;
  box-shadow: 0 0 0 3px rgba(254, 165, 48, 0.2);
}

.status-empty {
  background: #c7cdd4;
}

.node-meta {
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  flex-wrap: wrap;
  color: #1e2248;
}

.node-title {
  font-size: 13px;
  font-weight: 500;
  color: #1e2248;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.node-note {
  font-size: 11px;
  color: #8a96a6;
  background: #eef1f3;
  border-radius: 999px;
  padding: 2px 8px;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.tag-item {
  background: rgba(0, 94, 235, 0.12);
  color: #005eeb;
  border-radius: 999px;
  padding: 2px 6px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.price-input {
  height: 30px;
  font-size: 12px;
  width: 138px;
  min-width: 120px;
  flex-shrink: 0;
  background: #f4f7fb;
  border: 1px solid #dbe3f1 !important;
  border-radius: 6px !important;
  padding: 0 10px !important;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.price-input:focus {
  background: #ffffff;
  border-color: #005eeb !important;
  box-shadow: 0 0 0 3px rgba(0, 94, 235, 0.12);
}

.node-actions .price-input {
  margin-right: 2px;
}

.clear-btn {
  border: none;
  background: none;
  color: #c7cdd4;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  align-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  margin-left: -4px;
  flex-shrink: 0;
}

.clear-btn:hover {
  color: #ff5353;
}

.node-children {
  display: flex;
  flex-direction: column;
}

.tree-fade-enter-active,
.tree-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tree-fade-enter,
.tree-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.podbor-sidebar {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar-card {
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 8px 24px rgba(30, 34, 72, 0.08);
}

.sidebar-card h3 {
  font-size: 15px;
  font-weight: 600;
  color: #1e2248;
  margin-bottom: 12px;
}

.tips-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 16px;
  color: #636f7f;
  font-size: 13px;
}

.priced-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.priced-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #1e2248;
}

.priced-name {
  flex: 1;
}

.priced-value {
  font-weight: 600;
}

.empty-state {
  padding: 24px;
  text-align: center;
  color: #8a96a6;
}

.empty-state.small {
  padding: 12px;
  font-size: 13px;
}

@media (max-width: 1180px) {
  .podbor-layout {
    grid-template-columns: 1fr;
  }

  .podbor-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .sidebar-card {
    flex: 1 1 calc(50% - 8px);
  }
}

@media (max-width: 768px) {
  .podbor-card__header {
    flex-direction: column;
  }

  .podbor-card__controls {
    width: 100%;
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .control-group {
    min-width: 100%;
  }

  .podbor-summary {
    grid-template-columns: 1fr;
  }

  .node-row {
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px;
  }

  .tree-node::before,
  .tree-node > .node-row::before {
    display: none;
  }

  .node-info {
    flex: 1 1 100%;
    order: 1;
  }

  .node-meta {
    flex: 1 1 auto;
  }

  .node-actions {
    flex: 1 1 100%;
    order: 2;
    padding-left: 0;
    border-left: none;
    justify-content: space-between;
  }

  .node-actions .price-input {
    flex: 1 1 auto;
    min-width: 0;
  }

  .clear-btn {
    order: 2;
    align-self: center;
  }

  .podbor-tree {
    max-height: none;
  }

  .podbor-sidebar {
    flex-direction: column;
  }

  .sidebar-card {
    flex: 1 1 100%;
  }
}
</style>

